/**
 * Step 8 · Variogram Modelling.
 * Auto-runs omni-directional experimental variogram + spherical fit
 * on step entry. User can rerun with different lag / direction.
 */
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Play } from "@phosphor-icons/react";
import { WizardCard, StatusRow, KV, LoadingBanner } from "@/pages/wizard/_shared";


export default function VariogramStep({ projectId }) {
  const [lag, setLag] = useState(25);
  const [nLags, setNLags] = useState(15);
  const [fit, setFit] = useState(null);
  const [expArr, setExpArr] = useState([]);

  const runMut = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/variogram`, {
      lag_dist: lag, n_lags: nLags, direction: "omni", model: "spherical",
    }).then((r) => r.data),
    onSuccess: (d) => {
      setFit(d?.fit || null);
      setExpArr(d?.experimental || []);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message),
  });

  // Auto-run once on mount
  useMountOnce(() => runMut.mutate());

  const W = 340, H = 180, PAD = 22;
  const xs = expArr.map((p) => p.lag_center || p.h || 0);
  const ys = expArr.map((p) => p.gamma);
  const xMax = Math.max(1, ...xs);
  const yMax = Math.max(1e-9, ...ys, fit?.sill ? fit.sill * 1.2 : 0);
  const x = (v) => PAD + (v / xMax) * (W - 2 * PAD);
  const y = (v) => H - PAD - (v / yMax) * (H - 2 * PAD);

  return (
    <div className="max-w-4xl space-y-4">
      {fit
        ? <StatusRow tone="ok">
            Fitted spherical variogram — <b>nugget {fit.nugget?.toFixed?.(3)}</b>,
            {" "}<b>sill {fit.sill?.toFixed?.(3)}</b>, <b>range {fit.range?.toFixed?.(1)} m</b>
          </StatusRow>
        : <StatusRow tone="info">
            Auto-computing experimental variogram and spherical fit…
          </StatusRow>}

      <WizardCard title="Variogram parameters" testId="w-variogram-params">
        <Label className="text-[11px] font-semibold text-slate-600">Lag distance (m) — {lag}</Label>
        <Slider min={5} max={100} step={5} value={[lag]}
                onValueChange={([v]) => setLag(v)}
                data-testid="w-variogram-lag" />
        <Label className="text-[11px] font-semibold text-slate-600">Number of lags — {nLags}</Label>
        <Slider min={5} max={30} step={1} value={[nLags]}
                onValueChange={([v]) => setNLags(v)}
                data-testid="w-variogram-nlags" />
        <Button size="sm" onClick={() => runMut.mutate()}
                 disabled={runMut.isPending}
                 className="bg-brand-blue hover:bg-brand-blue/90 text-white"
                 data-testid="w-variogram-run">
          <Play size={12} className="mr-1" />
          {runMut.isPending ? "Fitting…" : "Refit"}
        </Button>
      </WizardCard>

      <WizardCard title="Experimental variogram + spherical fit" testId="w-variogram-chart">
        {runMut.isPending && <LoadingBanner label="Solving…" />}
        {expArr.length > 0 && (
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-56 bg-slate-50 rounded">
            <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="#94a3b8" strokeWidth="0.5" />
            <line x1={PAD} y1={PAD} x2={PAD} y2={H - PAD} stroke="#94a3b8" strokeWidth="0.5" />
            {fit?.sill != null && (
              <line x1={PAD} y1={y(fit.sill)} x2={W - PAD} y2={y(fit.sill)}
                     stroke="#dc2626" strokeWidth="0.6" strokeDasharray="3 2" />
            )}
            {expArr.map((p, i) => (
              <circle key={i} cx={x(p.lag_center || p.h || 0)}
                       cy={y(p.gamma)} r={3} fill="#1d4ed8" />
            ))}
          </svg>
        )}
        {fit && (
          <div className="grid grid-cols-3 gap-2 pt-2">
            <KV k="Nugget" v={fit.nugget?.toFixed?.(3)} />
            <KV k="Sill"   v={fit.sill?.toFixed?.(3)} />
            <KV k="Range"  v={`${fit.range?.toFixed?.(1) ?? "—"} m`} />
          </div>
        )}
      </WizardCard>
    </div>
  );
}


function useMountOnce(fn) {
  const [ran, setRan] = useState(false);
  if (!ran) {
    setRan(true);
    Promise.resolve().then(fn);
  }
}
